import {
  StaffModel,
  useGetStaffByRoles,
  useInvalidateUseGetStaffByRoles,
} from '@urbanx/agx.staff.client';
import { AgxAvatar, SelectChipOption, useToast } from '@urbanx/agx.ui-components2';
import { useContext, useEffect, useEffectEvent } from 'react';
import { RolesForTasks } from './RolesForTasks';
import { AuthContext } from '@urbanx/agx.auth.client';
import AppConfig from 'config/AppConfig';

export const unassignedOption = 'Unassigned';
// This hook can be mounted by multiple task views, so they share one replaceable error toast.
const ASSIGNEE_FILTER_LOAD_ERROR_TOAST_ID = 'assignee-filter-load-error';
const assigneeFilterInstances = new WeakMap<ReturnType<typeof useToast>, { count: number }>();

const formatName = (staffMember: StaffModel) => {
  return [staffMember.firstName, staffMember.lastName].filter((namePart) => !!namePart).join(' ');
};

export const useGetAssigneeOptions = (assignedToUserIds?: string[]) => {
  const { userAccount } = useContext(AuthContext);
  const toastContext = useToast();
  const { pushToast, removeToast } = toastContext;
  const currentUserId = AppConfig.userIdClaim ?? userAccount.user;

  const { data, isPending, isError, error, isFetching } = useGetStaffByRoles(RolesForTasks);
  const invalidateStaffByRoles = useInvalidateUseGetStaffByRoles();

  const notifyLoadResult = useEffectEvent(() => {
    if (isPending || isFetching) return;

    if (!isError) {
      removeToast(ASSIGNEE_FILTER_LOAD_ERROR_TOAST_ID);
      return;
    }

    pushToast(
      {
        message: 'Unable to load assignee filters',
        type: 'error',
        dismissible: true,
        action: {
          label: 'Retry',
          onClick: () => void invalidateStaffByRoles(RolesForTasks),
        },
      },
      ASSIGNEE_FILTER_LOAD_ERROR_TOAST_ID
    );
  });

  useEffect(() => {
    notifyLoadResult();
  }, [isPending, isFetching, isError, error]);

  useEffect(() => {
    const instances = assigneeFilterInstances.get(toastContext) ?? { count: 0 };
    instances.count += 1;
    assigneeFilterInstances.set(toastContext, instances);

    return () => {
      instances.count -= 1;
      if (instances.count === 0) {
        assigneeFilterInstances.delete(toastContext);
        toastContext.removeToast(ASSIGNEE_FILTER_LOAD_ERROR_TOAST_ID);
      }
    };
  }, [toastContext]);

  const sortedStaffMembers = [...(data ?? [])].sort((a, b) =>
    (a.firstName ?? '').localeCompare(b.firstName ?? '')
  );
  const me = sortedStaffMembers.find((s) => s.id === currentUserId || s.email === currentUserId);
  const remainingStaff = sortedStaffMembers.filter((s) => s !== me);
  const recombinedStaff = me ? [me, ...remainingStaff] : remainingStaff;

  const staff: SelectChipOption[] = recombinedStaff.map((staff) => {
    const photoPath = staff.photo?.publicPath;

    if (photoPath) {
      return {
        value: staff.email ?? '',
        label: formatName(staff),
        icon: <AgxAvatar key={staff.email} size="sm" type="image" imageUri={photoPath} />,
      };
    }

    const staffInitials = [staff.firstName, staff.lastName]
      .map((namePart) => namePart?.[0])
      .filter((namePart) => !!namePart)
      .join('')
      .toUpperCase();

    return {
      value: staff.email ?? '',
      label: formatName(staff),
      icon: <AgxAvatar size="sm" type="text-dark" text={staffInitials} />,
    };
  });

  const assigneeOptions = [
    {
      label: unassignedOption,
      value: unassignedOption,
      selected: assignedToUserIds?.includes(unassignedOption) ?? false,
      icon: undefined,
    },
    ...staff.map((s) => ({
      ...s,
      selected: assignedToUserIds?.includes(s.value) ?? false,
    })),
  ];

  return { assigneeOptions, assigneeOptionsPending: isPending };
};
